import { DateRangePickerWrapper } from 'components/Date/DateRangePicker';
import { tableStyles } from 'css/tableStyles';
import React, { useState } from 'react';
import DataTable from 'components/DataTable';
import { CentsToDollar, DATE_FORMATS, formatDate, getEmailsArray, showError } from 'utils';
import moment from 'moment';
import styles from './index.module.scss';
import ActionButton from 'components/ActionButton';
import ExportCSVButton from 'components/ExportCSVButton';
import { waitingForPaymentOrderReport } from 'services';
import Currency from 'components/Currency/Currency';
import AccountsDropdown from 'components/AccountsDropdown';
import CustomLink from 'components/TableLink';

const columns = [
  {
    name: 'Order Id',
    selector: 'objectId',
    cell: row => (
      <CustomLink
        text={row.objectId}
        linkTo={`/orders/${row.objectId}`}
      />
    ),
  },
  {
    name: 'Created At',
    selector: 'createdAt',
    sortable: false,
    format: (row) => formatDate(row.createdAt, DATE_FORMATS.MM_DD_YYYY_hh_mm_A),
  },
  {
    name: 'Amount',
    selector: 'total',
    format: (row) => <Currency amount={row.total / 100} default="-" />,
  },
  {
    name: 'User Name',
    selector: 'user',
    // eslint-disable-next-line react/display-name
    format: (row) => row.user?.full_name ?? '-',
  },
  {
    name: 'User Id',
    selector: 'user',
    // eslint-disable-next-line react/display-name
    cell: row => (
      <CustomLink
        text={row.user?.objectId}
        linkTo={`/users/${row.user?.objectId}`}
      />
    ),
  },
  {
    name: 'Account Name',
    selector: 'user.account',
    format: (row) => row.user?.account?.name ?? '-',
  },
  {
    name: 'Account Id',
    selector: 'user.account',
    // eslint-disable-next-line react/display-name
    cell: row => (
      <CustomLink
        text={row.user?.account?.objectId}
        linkTo={`/accounts/${row.user?.account?.objectId}`}
      />
    ),
  },
];

const columnsForCSV = [
  {
    label: 'Order ID',
    value: 'objectId',
  },
  {
    label: 'Created At',
    value: (row) => formatDate(row.createdAt, DATE_FORMATS.MM_DD_YYYY_hh_mm_A),
  },
  {
    label: 'Amount ($)',
    value: row => CentsToDollar(row.total),
  },
  {
    label: 'Waiting For Payment',
    value: 'waitingForPayment',
  },
  {
    label: 'Account Id',
    value: row => row.user?.account?.objectId ?? '-',
  },
  {
    label: 'Account Name',
    value: (row) => row.user?.account?.name ?? '-',
  },
  {
    label: 'User Id',
    value: row => row.user?.objectId ?? '-',
  },
  {
    label: 'User Name',
    value: row => row.user?.full_name ?? '-',
  },
  {
    label: 'Order Pdf Link',
    value: row => `${process.env.REACT_APP_WEB_APP_URL}/orders/${row.objectId}`,
  },
];

const WaitingForPaymentOrderReport = props => {
  const [data, setData] = useState<Record<string, any>[]>([]);
  const [startDate, setStartDate] = useState<string>(moment().subtract(1, 'month').toISOString());
  const [endDate, setEndDate] = useState<string>(moment().toISOString());
  const [email, setEmail] = useState<string>('');
  const [sendEmail, setSendEmail] = useState<boolean>(false);
  const [accountId, setAccountId] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [buttonLoading, setButtonLoading] = useState<boolean>(false);

  const fetchOrderReportFn = async () => {
    setLoading(true);
    setButtonLoading(true);
    const data = {
      accountId,
      startDate,
      endDate,
      emails: getEmailsArray(email),
      sendEmail,
    };
    try {
      const response = await waitingForPaymentOrderReport(data);
      setData(response);
    } catch (e) {
      showError(e);
    } finally {
      setLoading(false);
      setButtonLoading(true);
    }
  };

  const onDateSelectionChange = ({ startDate, endDate }: { startDate: Date; endDate: Date }) => {
    setStartDate(moment(startDate).toISOString());
    setEndDate(moment(endDate).toISOString());
  };

  const getDataTable = () => {
    return (
      <DataTable
        noHeader={true}
        data={data}
        columns={columns}
        customStyles={tableStyles}
      />);
  };

  const getHeader = () => {
    return (
      <div className={styles['header']}>
        <div className={styles['heading']}>Waiting For Payment Orders Report</div>
        <ExportCSVButton
          disabled={!buttonLoading}
          fileName={'Waiting For Payment Orders Report'}
          data={data}
          columnsForCSV={columnsForCSV}
        />
      </div>
    );
  };

  const getFilterOptions = () => {
    return (
      <>
        <div>
          <div className={styles['label']}>Account ID</div>
          <AccountsDropdown
            value={accountId}
            onChange={setAccountId}
          />
        </div>
        <div>
          <div className={styles['label']}>Date Range</div>
          <DateRangePickerWrapper
            disabled={loading}
            startDate={startDate}
            endDate={endDate}
            onDatesChange={onDateSelectionChange}
          />
        </div>
        <div>
          <div className={styles['label']}>Emails</div>
          <input
            className={'form-control'}
            disabled={loading || !sendEmail}
            value={email}
            onChange={e => setEmail(e.target.value)}
          />
          <div className={styles['helper']}>Use commas to separate emails</div>
        </div>
        <div className={'d-flex my-auto'}>
          <input
            type={'checkbox'}
            disabled={loading}
            checked={sendEmail}
            onChange={e => setSendEmail(e.target.checked)}
          />
          <div>Send Email</div>
        </div>
      </>
    );
  };

  const getActionButtons = () => {
    return (
      <ActionButton
        text={'Submit'}
        active={!loading}
        isLoading={false}
        onClick={fetchOrderReportFn}
        customClass={'ml-4 my-auto'}
      />
    );
  };

  return (
    <div>
      {getHeader()}
      <div className={styles['filter-container']}>
        {getFilterOptions()}
        {getActionButtons()}
      </div>
      {getDataTable()}
    </div>
  );
};

export default WaitingForPaymentOrderReport;